import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { push } from 'connected-react-router'
import { layouts, common } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { EnvironmentUtils, UserUtils } from 'utils'
import { useLazySelector } from 'hooks'
import Skeleton, { SkeletonVariant } from 'components/common/Skeleton'
import HomeRoutes from 'modules/Home/routing/routes'
import routes from './routes'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

type RouteState = {
  sessionId?: string
  assessmentId?: string
}

const AssessmentPlayer: React.FC = () => {
  const assessmentPlayerRef = useRef<HTMLIFrameElement>(null)
  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation
    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })
  const dispatch = useDispatch()
  const { simulatorDashboardForAdministratorsAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')
  const { state } = useLocation<RouteState>()
  const { sessionId, assessmentId } = state ?? {}

  useEffect(() => {
    const exitProgramBuilder = (e: MessageEvent) => {
      if (e.data.type === 'EXIT_ASSESSMENT_PLAYER') {
        dispatch(push(HomeRoutes.root))
      }
    }
    window.addEventListener('message', exitProgramBuilder)
    return () => {
      window.removeEventListener('message', exitProgramBuilder)
    }
  }, [dispatch, simulatorDashboardForAdministratorsAvailable])

  const sendTokenAndUserInfo = () => {
    if (assessmentPlayerRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login, name } = UserUtils.getUserAccountInfo()
      assessmentPlayerRef.current.contentWindow?.postMessage(
        {
          type: 'OLP_ASSESSMENT_PLAYER',
          sessionToken,
          connectedPerson,
          username: name ?? login ?? '',
          customerId,
        },
        window.cc.assessmentPlayer ?? ''
      )
    }
  }

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <iframe
            title='Assessment Player'
            ref={assessmentPlayerRef}
            src={
              sessionId
                ? `${window.cc.assessmentPlayer}/${routes.adaptiveLearningAssessment}/?sessionId=${sessionId}`
                : `${window.cc.assessmentPlayer}/?assessmentId=${assessmentId}`
            }
            onLoad={sendTokenAndUserInfo}
            width='100%'
            height='100%'
            style={{ overflow: 'auto' }}
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default AssessmentPlayer
